import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ChipColor } from './loanReviewChipColors';
import { overallStatusMeta, statusChipColor } from './loanReviewChipColors';
import type { LoanReviewServiceSection } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { displayStatusLabel } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';
import HubRoundedIcon from '@mui/icons-material/HubRounded';

/** One card per upstream service: what we received, and what Loan Review did with it. */
export function LoanReviewServiceTable({ section }: { section: LoanReviewServiceSection }) {
  const overall: { label: string; color: ChipColor } = section.rows.length
    ? overallStatusMeta(section.rows.map((r) => r.status))
    : { label: 'No data', color: 'default' };

  return (
    <ApplicationSectionCard
      title={section.title}
      subtitle={section.rows.length ? `${section.rows.length} checks` : 'No signals recorded for this service on this run.'}
      icon={<HubRoundedIcon fontSize="small" />}
      data-testid={`loan-review-service-${section.key}`}
      endAdornment={
        section.rows.length ? (
          <Chip size="small" color={overall.color} label={overall.label} sx={{ fontWeight: 700, height: 22 }} />
        ) : undefined
      }
    >
      {section.agentLine ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            Agent 1 read:
          </Box>{' '}
          {section.agentLine.value}
        </Typography>
      ) : null}
      {section.rows.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', borderBottomWidth: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell component="th">Field</TableCell>
                <TableCell component="th">What we received</TableCell>
                <TableCell component="th">What Loan Review did</TableCell>
                <TableCell component="th">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {section.rows.map((row, i) => (
                <TableRow key={`${row.field}-${i}`}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.field}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{row.received}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', color: 'text.secondary' }}>{row.action}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusChipColor(row.status)}
                      label={displayStatusLabel(row.status)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </ApplicationSectionCard>
  );
}
